import { AttachmentRule, Behavior, BehaviorConstructor } from "./behaviors/types";

// World Manager - handles registration, lifecycle, and ticking
export class WorldMaker {
  private behaviorClasses: BehaviorConstructor[] = [];
  private activeBehaviors: Map<Element, Map<BehaviorConstructor, Behavior>> = new Map();
  private elementParentMap: Map<Element, Element | null> = new Map(); // Track element parents for change detection
  private observer: MutationObserver;
  private lastTime: number = 0;
  private tickIntervalId: number | null = null;
  private isRunning: boolean = false;
  private rootElement: Element | null = null;

  // Stats tracking
  private stats = {
    tickTime: 0,
    fps: 0,
    elementCount: 0,
    behaviorCount: 0,
    behaviorsTicked: 0,
    systemCount: 0,
    systemsTicked: 0,
    msPerBehaviorTick: 0,
    msPerSystemTick: 0,
    lastStatsUpdate: 0,
    frameCount: 0,
    totalTickTime: 0,
  };
  private statsElement: HTMLElement | null = null;

  constructor() {
    this.observer = new MutationObserver((mutations) => this.handleMutations(mutations));
    this.tick = this.tick.bind(this);
  }

  // Register a behavior class
  registerBehavior(behaviorClass: BehaviorConstructor): void {
    this.behaviorClasses.push(behaviorClass);
    const rules = behaviorClass.getAttachmentRules();
    console.log(`Registered behavior: ${behaviorClass.name} with rules:`, rules);
  }

  // Helper method: Get behavior instance by class from an element
  getBehavior<T extends Behavior>(
    element: Element,
    behaviorClass: new (element: Element, world: WorldMaker) => T,
  ): T | null {
    const elementBehaviors = this.activeBehaviors.get(element);
    if (!elementBehaviors) return null;

    return elementBehaviors.get(behaviorClass as any) as T | null;
  }

  // Helper method: Check if element has a specific behavior
  hasBehavior(element: Element, behaviorClass: BehaviorConstructor): boolean {
    const elementBehaviors = this.activeBehaviors.get(element);
    return elementBehaviors?.has(behaviorClass) ?? false;
  }

  // Helper method: Get all behaviors on an element
  getAllBehaviors(element: Element): Behavior[] {
    const elementBehaviors = this.activeBehaviors.get(element);
    return elementBehaviors ? Array.from(elementBehaviors.values()) : [];
  }

  // Helper method: Get all elements that have a specific behavior
  getElementsWithBehavior(behaviorClass: BehaviorConstructor): Element[] {
    const elements: Element[] = [];
    this.activeBehaviors.forEach((behaviorMap, element) => {
      if (behaviorMap.has(behaviorClass)) {
        elements.push(element);
      }
    });
    return elements;
  }

  // Helper method: Get the root element
  getRootElement(): Element | null {
    return this.rootElement;
  }

  // Initialize stats display
  private initStatsDisplay(): void {
    // Create stats display element if it doesn't exist
    this.statsElement = document.getElementById("world-stats");
    if (!this.statsElement) {
      this.statsElement = document.createElement("div");
      this.statsElement.id = "world-stats";
      this.statsElement.style.cssText = `
        position: fixed;
        top: 10px;
        left: 10px;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 10px;
        font-family: monospace;
        font-size: 12px;
        border-radius: 5px;
        z-index: 1000;
        min-width: 200px;
      `;
      document.body.appendChild(this.statsElement);
    }
  }

  // Update stats display
  private updateStatsDisplay(): void {
    if (!this.statsElement) return;

    const totalElements = this.rootElement ? this.rootElement.querySelectorAll("*").length + 1 : 0;

    this.statsElement.innerHTML = `
      <div><strong>World Stats</strong></div>
      <div>FPS: ${this.stats.fps.toFixed(1)}</div>
      <div>Tick Time: ${this.stats.tickTime.toFixed(2)}ms</div>
      <div>MS per Behavior: ${this.stats.msPerBehaviorTick.toFixed(3)}ms</div>
      <div>MS per System: ${this.stats.msPerSystemTick.toFixed(3)}ms</div>
      <div>Elements: ${totalElements}</div>
      <div>Elements w/ Behaviors: ${this.stats.behaviorCount}</div>
      <div>Behaviors Ticked: ${this.stats.behaviorsTicked}</div>
      <div>Systems Ticked: ${this.stats.systemsTicked}</div>
      <div>Behavior Classes: ${this.behaviorClasses.length}</div>
      <div>Disconnected: ${this.getDisconnectedElementCount()}</div>
    `;
  }

  // Get current stats (public method for external access)
  getStats() {
    return { ...this.stats };
  }

  // Public method: Force cleanup of disconnected elements
  forceCleanupDisconnectedElements(): number {
    const beforeCount = this.activeBehaviors.size;
    this.cleanupDisconnectedElements();
    const afterCount = this.activeBehaviors.size;
    const cleanedUp = beforeCount - afterCount;

    if (cleanedUp > 0) {
      console.log(`Cleaned up behaviors for ${cleanedUp} disconnected elements`);
    }

    return cleanedUp;
  }

  // Debug method: Get count of active behaviors
  getActiveBehaviorCount(): number {
    return this.activeBehaviors.size;
  }

  // Debug method: Get count of disconnected elements with behaviors
  getDisconnectedElementCount(): number {
    let count = 0;
    this.activeBehaviors.forEach((behaviorMap, element) => {
      if (!element.isConnected) {
        count++;
      }
    });
    return count;
  }

  // Helper method: Clean up behaviors for elements no longer in the DOM
  private cleanupDisconnectedElements(): void {
    const elementsToRemove: Element[] = [];

    this.activeBehaviors.forEach((behaviorMap, element) => {
      // Check if element is still connected to the DOM
      if (!element.isConnected) {
        elementsToRemove.push(element);
      }
    });

    // Remove behaviors for disconnected elements
    elementsToRemove.forEach((element) => {
      console.log("Cleaning up behaviors for disconnected element:", element);
      this.removeElementBehaviors(element);
    });
  }

  // Check if an element matches a behavior's attachment rules
  private elementMatchesRules(element: Element, rules: AttachmentRule[]): boolean {
    return rules.some((rule) => {
      if (rule.type === "element") {
        return element.tagName.toLowerCase() === rule.selector.toLowerCase();
      } else if (rule.type === "class") {
        return element.classList.contains(rule.selector);
      } else if (rule.type === "root") {
        return element === this.rootElement;
      }
      return false;
    });
  }

  // Helper method: Check if an element matches a CSS selector
  private elementMatchesSelector(element: Element, selector: string): boolean {
    try {
      return element.matches(selector);
    } catch (error) {
      console.warn(`Invalid CSS selector: ${selector}`, error);
      return false;
    }
  }

  // Start observing the DOM for changes
  startObserving(root: Element): void {
    this.rootElement = root;
    this.observer.observe(root, {
      attributes: true,
      attributeOldValue: true,
      subtree: true,
      childList: true,
    });

    // Initialize stats display
    this.initStatsDisplay();
    // Start the tick loop
    this.lastTime = Date.now();
    this.stats.lastStatsUpdate = this.lastTime;

    this.startTickLoop();

    // Check existing elements for behaviors
    this.checkElementForBehaviors(root);
    root.querySelectorAll("*").forEach((el) => this.checkElementForBehaviors(el));
  }

  // Stop observing and clean up
  stopObserving(): void {
    this.observer.disconnect();
    this.stopTickLoop();

    // Clean up stats display
    if (this.statsElement && this.statsElement.parentNode) {
      this.statsElement.parentNode.removeChild(this.statsElement);
      this.statsElement = null;
    }
    // Detach all behaviors
    this.activeBehaviors.forEach((behaviorMap) => {
      behaviorMap.forEach((behavior) => behavior.onDetach());
    });
    this.activeBehaviors.clear();
  }

  private startTickLoop(): void {
    if (this.isRunning) return;

    this.isRunning = true;

    // Use setInterval for consistent behavior across all environments
    this.tickIntervalId = setInterval(this.tick, 50) as any;
  }

  private stopTickLoop(): void {
    this.isRunning = false;

    if (this.tickIntervalId !== null) {
      clearInterval(this.tickIntervalId);
      this.tickIntervalId = null;
    }
  }

  private handleMutations(mutations: MutationRecord[]): void {
    for (const mutation of mutations) {
      if (mutation.type === "attributes") {
        const element = mutation.target as Element;
        const attributeName = mutation.attributeName!;
        const oldValue = mutation.oldValue;
        const newValue = element.getAttribute(attributeName);

        // Notify all behaviors on this element about the attribute change
        const elementBehaviors = this.activeBehaviors.get(element);
        if (elementBehaviors) {
          elementBehaviors.forEach((behavior) => {
            try {
              behavior.onAttributeChange(attributeName, oldValue, newValue);
            } catch (error) {
              console.error("Error in behavior onAttributeChange:", error);
            }
          });
        }

        // Check if behaviors need to be attached/detached due to class changes
        if (attributeName === "class") {
          this.checkElementForBehaviors(element);
        }
      } else if (mutation.type === "childList") {
        // Handle removed nodes
        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            console.log("Element removed from DOM, cleaning up behaviors:", element);

            this.removeElementBehaviors(element);
          }
        });
        // Handle added nodes
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;

            // Check for behaviors first
            this.checkElementForBehaviors(element);
            element.querySelectorAll("*").forEach((el) => this.checkElementForBehaviors(el));
          }
        });
      }
    }

    // Additional safety check: clean up any disconnected elements that might have been missed
    // This is a fallback in case the MutationObserver missed some removals
    setTimeout(() => {
      this.cleanupDisconnectedElements();
    }, 0);
  }

  private checkElementForBehaviors(element: Element): void {
    this.behaviorClasses.forEach((behaviorClass) => {
      const rules = behaviorClass.getAttachmentRules();
      const shouldHaveBehavior = this.elementMatchesRules(element, rules);
      const elementBehaviors = this.activeBehaviors.get(element);
      const hasBehavior = elementBehaviors?.has(behaviorClass) ?? false;

      if (shouldHaveBehavior && !hasBehavior) {
        this.attachBehavior(element, behaviorClass);
      } else if (!shouldHaveBehavior && hasBehavior) {
        this.detachBehavior(element, behaviorClass);
      }
    });
  }

  private attachBehavior(element: Element, behaviorClass: BehaviorConstructor): void {
    if (!this.activeBehaviors.has(element)) {
      this.activeBehaviors.set(element, new Map());
    }

    const behavior = new behaviorClass(element, this);
    this.activeBehaviors.get(element)!.set(behaviorClass, behavior);
    behavior.onAttach();

    // Update the behaviour attribute for debugging
    this.updateBehaviorAttribute(element);
  }

  private detachBehavior(element: Element, behaviorClass: BehaviorConstructor): void {
    const elementBehaviors = this.activeBehaviors.get(element);
    if (elementBehaviors) {
      const behavior = elementBehaviors.get(behaviorClass);
      if (behavior) {
        behavior.onDetach();
        elementBehaviors.delete(behaviorClass);

        if (elementBehaviors.size === 0) {
          this.activeBehaviors.delete(element);
        }

        // Update the behaviour attribute for debugging
        this.updateBehaviorAttribute(element);
      }
    }
  }

  private updateBehaviorAttribute(element: Element): void {
    const elementBehaviors = this.activeBehaviors.get(element);
    if (elementBehaviors && elementBehaviors.size > 0) {
      const behaviorNames = Array.from(elementBehaviors.keys()).map((behaviorClass) =>
        behaviorClass.name.replace(/Behavior$/, "").toLowerCase(),
      );
      element.setAttribute("behaviour", behaviorNames.join(" "));
    } else {
      element.removeAttribute("behaviour");
    }
  }

  private removeElementBehaviors(element: Element): void {
    const elementBehaviors = this.activeBehaviors.get(element);
    if (elementBehaviors) {
      elementBehaviors.forEach((behavior) => behavior.onDetach());
      this.activeBehaviors.delete(element);
      // Remove the behaviour attribute when all behaviors are removed
      element.removeAttribute("behaviour");
    }

    // Also check child elements
    element.querySelectorAll("*").forEach((child) => {
      this.removeElementBehaviors(child);
    });
  }

  private tick(): void {
    if (!this.isRunning) return;
    const tickStartTime = performance.now();
    const currentTime = performance.now();
    const deltaTime = (currentTime - this.lastTime) / 1000; // Convert to seconds
    this.lastTime = currentTime;

    // Reset counters for this frame
    this.stats.behaviorsTicked = 0;
    this.stats.systemsTicked = 0;

    // Periodically clean up disconnected elements (every ~1 second)
    if (Math.floor(currentTime / 1000) !== Math.floor((currentTime - deltaTime * 1000) / 1000)) {
      this.cleanupDisconnectedElements();
    }

    // Tick all active systems first
    const systemTickStartTime = performance.now();
    const systemTickEndTime = performance.now();

    // Tick all active behaviors
    this.activeBehaviors.forEach((behaviorMap, element) => {
      // Skip if element is no longer connected to the DOM
      if (!element.isConnected) {
        return;
      }

      behaviorMap.forEach((behavior) => {
        try {
          behavior.tick(deltaTime);
          this.stats.behaviorsTicked++;
        } catch (error) {
          console.error(`Error in behavior ${behavior.constructor.name} tick:`, error);
        }
      });
    });

    // Calculate tick times
    const tickEndTime = performance.now();
    const tickTime = tickEndTime - tickStartTime;
    const systemTickTime = systemTickEndTime - systemTickStartTime;

    // Update stats
    this.stats.tickTime = tickTime;
    this.stats.totalTickTime += tickTime;
    this.stats.frameCount++;
    this.stats.behaviorCount = this.activeBehaviors.size;

    // Calculate average ms per behavior/system tick
    this.stats.msPerBehaviorTick =
      this.stats.behaviorsTicked > 0 ? (tickTime - systemTickTime) / this.stats.behaviorsTicked : 0;
    this.stats.msPerSystemTick =
      this.stats.systemsTicked > 0 ? systemTickTime / this.stats.systemsTicked : 0;

    // Update FPS and display stats every 500ms
    if (currentTime - this.stats.lastStatsUpdate >= 500) {
      const timeDiff = (currentTime - this.stats.lastStatsUpdate) / 1000;
      this.stats.fps = this.stats.frameCount / timeDiff;
      this.stats.frameCount = 0;
      this.stats.lastStatsUpdate = currentTime;
      this.updateStatsDisplay();
    }

    // No need to schedule next tick - setInterval handles this automatically
  }
}
